// The app's browser-side logger.
//
//   log.info('board opened', { boardId });          // context is a flat object of short values
//   log.error('request failed', describeError(e));  // warnings and errors are also sent to the server
//
// - Quiet by default: debug is hidden everywhere, and info is hidden in production. Turn the detail on in a
//   browser with  localStorage.setItem('logLevel', 'debug')  (and reload); remove the key to go back.
// - Warnings and errors are forwarded to the API (POST /api/clientLogs), which writes them alongside its own
//   logs, so they reach ELK instead of staying in a user's console. Uncaught errors and unhandled promise
//   rejections are captured the same way (see installGlobalErrorLogging).
// - Never put credentials, tokens or personal data in a message or its context.

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

type Context = Record<string, unknown>;

const LEVEL_RANK: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

const readStoredLevel = (): LogLevel | null => {
    try {
        const stored = localStorage.getItem('logLevel');
        return stored && stored in LEVEL_RANK ? (stored as LogLevel) : null;
    } catch {
        return null; // storage can be unavailable (private windows, blocked site data)
    }
};

// Running from this machine counts as development: show info there, only warnings and errors elsewhere.
const isLocalDevelopment = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

const activeLevel = (): LogLevel => readStoredLevel() ?? (isLocalDevelopment ? 'info' : 'warn');

// --- Forwarding to the server -----------------------------------------------------------------------------------

type Outgoing = {
    level: 'warn' | 'error';
    message: string;
    time: string;
    page: string;
    context?: Context;
    error?: { type?: string, message?: string, stack?: string };
};

const FLUSH_DELAY_MS = 2000;
const MAX_BATCH = 10;
const MAX_PER_MINUTE = 20;
const DUPLICATE_WINDOW_MS = 30_000;

let queue: Outgoing[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let sentThisMinute = 0;
let minuteStartedAt = Date.now();
const recentlySent = new Map<string, number>();

const serializeError = (error: unknown) => {
    if (error instanceof Error) return { type: error.name, message: error.message, stack: error.stack };
    return { message: typeof error === 'string' ? error : JSON.stringify(error) };
};

async function flush() {
    flushTimer = undefined;
    const batch = queue.splice(0, MAX_BATCH);
    if (batch.length === 0) return;
    if (queue.length > 0) flushTimer = setTimeout(flush, FLUSH_DELAY_MS);

    try {
        // Imported here, not at the top: the API proxy depends on services that log, and a static import
        // would make a cycle.
        const { default: getApiProxy } = await import('../proxies/apiProxy');
        const api = await getApiProxy();
        await api.post('api/clientLogs', { entries: batch });
    } catch {
        // Reporting a problem must never cause one, and logging this failure would just try to report it again.
    }
}

function forward(entry: Outgoing) {
    const now = Date.now();
    if (now - minuteStartedAt > 60_000) {
        minuteStartedAt = now;
        sentThisMinute = 0;
    }
    if (sentThisMinute >= MAX_PER_MINUTE) return;

    // The same failure repeating (a render loop, a poller) is reported once per window, not hundreds of times.
    const fingerprint = `${entry.level}|${entry.message}|${entry.error?.message ?? ''}`;
    const last = recentlySent.get(fingerprint);
    if (last !== undefined && now - last < DUPLICATE_WINDOW_MS) return;
    recentlySent.set(fingerprint, now);
    if (recentlySent.size > 200) recentlySent.clear();

    sentThisMinute++;
    queue.push(entry);
    if (queue.length >= MAX_BATCH) {
        void flush();
    } else if (!flushTimer) {
        flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
    }
}

// --- Writing ------------------------------------------------------------------------------------------------------

const consoleMethod: Record<LogLevel, 'debug' | 'info' | 'warn' | 'error'> = {
    debug: 'debug', info: 'info', warn: 'warn', error: 'error',
};
const consoleStyle: Record<LogLevel, string> = {
    debug: 'color:#888', info: 'color:#2e7d32', warn: 'color:#ed6c02', error: 'color:#d32f2f;font-weight:bold',
};

function write(level: LogLevel, message: string, context?: Context) {
    const at = new Date();

    if (LEVEL_RANK[level] >= LEVEL_RANK[activeLevel()]) {
        const time = at.toISOString().slice(11, 23);
        const args: unknown[] = [`%c${time} ${level.toUpperCase().padEnd(5)}%c ${message}`, consoleStyle[level], 'color:inherit'];
        if (context && Object.keys(context).length) args.push(context);
        console[consoleMethod[level]](...args);
    }

    if (level === 'warn' || level === 'error') {
        // An Error under the `error` key is sent as its own field so the server records type and stack.
        const { error, ...rest } = context ?? {};
        forward({
            level,
            message,
            time: at.toISOString(),
            page: window.location.pathname, // path only: query strings can carry tokens
            context: Object.keys(rest).length ? rest : undefined,
            error: error !== undefined ? serializeError(error) : undefined,
        });
    }
}

export const log = {
    debug: (message: string, context?: Context) => write('debug', message, context),
    info: (message: string, context?: Context) => write('info', message, context),
    warn: (message: string, context?: Context) => write('warn', message, context),
    error: (message: string, context?: Context) => write('error', message, context),
};

// Turns an error from an API call into loggable fields: what was called, what came back, and the server's
// X-Request-Id, which finds the matching lines in the API's logs. Safe on errors that have no response
// (a dropped connection), unlike reading `error.response.data` directly.
export function describeError(error: any, operation?: string): Context {
    const response = error?.response;
    return {
        ...(operation ? { operation } : {}),
        status: response?.status,
        method: error?.config?.method?.toUpperCase(),
        url: error?.config?.url,
        requestId: response?.headers?.['x-request-id'],
        serverMessage: response?.data?.message,
        reason: error?.message,
    };
}

let globalHandlersInstalled = false;

// Reports errors that nothing caught. Call once at startup.
export function installGlobalErrorLogging() {
    if (globalHandlersInstalled) return;
    globalHandlersInstalled = true;

    window.addEventListener('error', (event) => {
        log.error('uncaught error', { error: event.error ?? event.message, source: event.filename, line: event.lineno });
    });
    window.addEventListener('unhandledrejection', (event) => {
        log.error('unhandled promise rejection', { error: event.reason });
    });
}
